// src/app/dashboard/page.tsx

"use client";

import {
  CheckCircleOutlined,
  HistoryOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { App, Button, Drawer, Result, Spin, Steps } from "antd";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import AddNewDocument from "@/components/AddNewDocument";
import ConfirmationModal from "@/components/ConfirmationModal";
import CreditInformation from "@/components/CreditInformation";
import CreditPurchase from "@/components/CreditPurchase";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProcessedDocumentResultsViewer from "@/components/ProcessedDocumentResultsViewer";
import ProgressTracker from "@/components/ProgressTracker";
import PromoCodes from "@/components/PromoCodes";
import ViewPastProcessingJobs from "@/components/ViewPastProcessingJobs";
import useAppStore from "@/store/useAppStore";

export default function Dashboard() {
  const { message } = App.useApp();
  const router = useRouter();
  const { data: session, status } = useSession();
  const progressSectionRef = useRef<HTMLDivElement>(null);
  const resultsSectionRef = useRef<HTMLDivElement>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const {
    documentFile,
    documentInfo,
    generateMultipleChoiceQuiz,
    generateShortAnswerQuiz,
    generateAnkiFlashcards,
    generatePresentationSlides,
    generateSummary,
    generateMindmap,
    generateReadabilityAnalysis,
    generateLessonPlan,
    lessonPlanTargetAudience,
    presentationTheme,
    jobStatus,
    showPreview,
    showPastJobs,
    jobCompleted,
    currentJobId,
    currentRequestId,
    creditCost,
    showConfirmModal,
    costBreakdown,
    userInfo,
    isProcessingDocument,
    isApplyingPromoCode,
    isLoadingUserInfo,
    jobCompletionHandled,
    isCheckingStatus,
    resultsReady,
    error,
    handleSubmit,
    confirmProcessing,
    handleJobComplete,
    refetchUserInfo,
    applyPromoCodeWrapper,
    setShowConfirmModal,
    setSession,
    setError,
    resetJobState,
    fetchJobStatus,
    updateJobStatus,
    processedFiles,
    fetchProcessedFiles,
    setProcessedFiles,
    scrollToResults,
    setScrollToResults,
    isLoadingFiles,
    setShowPastJobs,
  } = useAppStore();

  const MemoizedCreditPurchase = React.useMemo(
    () => <CreditPurchase onSuccess={refetchUserInfo} />,
    [refetchUserInfo],
  );

  useEffect(() => {
    if (error) {
      resetJobState();
    }
  }, [error, resetJobState]);

  const adminFetcher = useCallback(async (url: string) => {
    const response = await axios.get(url);
    return response.data;
  }, []);

  const { data: adminVerify } = useSWR(
    status === "authenticated" && session ? "/api/admin/verify" : null,
    adminFetcher,
    {
      revalidateOnFocus: false,
    },
  );

  const isAdmin = Boolean(adminVerify?.isAdmin);

  useEffect(() => {
    if (status === "authenticated" && session) {
      setSession(session);
      refetchUserInfo();
    } else if (status === "unauthenticated") {
      router.push("/");
    }
  }, [status, session, setSession, router, refetchUserInfo]);

  useEffect(() => {
    if (currentJobId && jobCompleted && !resultsReady && !isLoadingFiles) {
      fetchProcessedFiles(currentJobId);
    }
  }, [currentJobId, jobCompleted, resultsReady, isLoadingFiles, fetchProcessedFiles]);

  useEffect(() => {
    if (scrollToResults && progressSectionRef.current && (isProcessingDocument || jobCompleted)) {
      progressSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setScrollToResults(false);
    }
  }, [scrollToResults, setScrollToResults, isProcessingDocument, jobCompleted]);

  useEffect(() => {
    if (resultsReady && resultsSectionRef.current) {
      setTimeout(() => {
        resultsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [resultsReady]);

  useEffect(() => {
    if (!currentJobId || jobCompleted) {
      return;
    }

    let eventSource: EventSource | null = null;
    let pollInterval: number | null = null;
    let fallbackTimer: number | null = null;
    let receivedFirstEvent = false;

    const startPollingFallback = () => {
      if (pollInterval) return;
      pollInterval = window.setInterval(() => {
        fetchJobStatus();
      }, 2000);
    };

    try {
      const encodedJobId = encodeURIComponent(currentJobId);
      eventSource = new EventSource(`/api/job_status/${encodedJobId}?stream=1`);

      eventSource.addEventListener("job_status", (evt) => {
        receivedFirstEvent = true;
        try {
          const data = JSON.parse((evt as MessageEvent).data);
          updateJobStatus(data);
        } catch (_) {
          // Fall back to polling if we can't parse the stream payload.
          eventSource?.close();
          eventSource = null;
          startPollingFallback();
        }
      });

      eventSource.onerror = () => {
        eventSource?.close();
        eventSource = null;
        startPollingFallback();
      };

      // If we don't receive any events quickly, assume streaming isn't available.
      fallbackTimer = window.setTimeout(() => {
        if (!receivedFirstEvent) {
          eventSource?.close();
          eventSource = null;
          startPollingFallback();
        }
      }, 5000);
    } catch (_) {
      startPollingFallback();
    }

    return () => {
      eventSource?.close();
      if (pollInterval) window.clearInterval(pollInterval);
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
    };
  }, [currentJobId, jobCompleted, fetchJobStatus, updateJobStatus]);

  // Loading state
  if (status === "loading") {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-lg">
            <svg
              className="w-7 h-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <Spin size="large" />
          <p className="text-gray-500 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center p-8 bg-white rounded-2xl shadow-xl">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-7 h-7 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <p className="text-gray-900 text-xl font-semibold">Access Denied</p>
          <p className="text-gray-500 mt-2">Please sign in to continue</p>
          <Link href="/">
            <Button type="primary" size="large" className="mt-4">
              Go to Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const userName = session?.user?.name || "User";
  const userInitial = userName.charAt(0).toUpperCase();

  const showWorkflowStepper = Boolean(
    documentFile ||
      documentInfo ||
      currentJobId ||
      isProcessingDocument ||
      jobCompleted ||
      resultsReady,
  );
  const workflowCurrentStep = (() => {
    if (!documentFile) return 0; // Upload
    if (!documentInfo) return 0; // Still analyzing
    if (showConfirmModal) return 2; // Review
    if (resultsReady) return 4; // Results
    if (isProcessingDocument || currentJobId || jobCompleted) return 3; // Processing
    return 1; // Options
  })();

  return (
    <ErrorBoundary
      componentName="Dashboard"
      fallback={
        <div className="flex justify-center items-center h-screen bg-gray-50">
          <Result
            status="error"
            title="Something went wrong"
            subTitle="Please refresh and try again."
          />
        </div>
      }
    >
      <div className="min-h-screen bg-gray-50">
        {/* Desktop Header */}
        <header className="hidden lg:block sticky top-0 z-50 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                href="/"
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-sm">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <span className="text-lg font-semibold text-gray-900">FixMyDocuments</span>
              </Link>
              <span className="text-gray-300">|</span>
              <span className="text-gray-600 font-medium">Dashboard</span>
            </div>

            <div className="flex items-center gap-3">
              {isAdmin && (
                <Link href="/admin">
                  <button className="h-9 px-4 text-sm font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors flex items-center gap-2">
                    <SettingOutlined className="text-sm" />
                    Admin
                  </button>
                </Link>
              )}
              <div className="flex items-center gap-3 pl-3 border-l border-gray-200">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white text-sm font-semibold">
                  {userInitial}
                </div>
                <span className="text-sm font-medium text-gray-700">{userName}</span>
                <button
                  onClick={() => signOut()}
                  className="h-8 w-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
                  title="Sign out"
                >
                  <LogoutOutlined className="text-sm" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Header */}
        <header
          className="lg:hidden sticky top-0 z-50 bg-white border-b border-gray-200"
          style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        >
          <div className="px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <span className="text-base font-semibold text-gray-900">Dashboard</span>
            </div>
            <button
              onClick={() => setDrawerVisible(true)}
              className="w-10 h-10 -mr-2 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <MenuOutlined className="text-lg" />
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Workflow Stepper */}
          {showWorkflowStepper && (
            <div className="mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
              <div className="hidden md:block">
                <Steps
                  current={workflowCurrentStep}
                  size="small"
                  items={[
                    { title: "Upload" },
                    { title: "Options" },
                    { title: "Review" },
                    { title: "Processing" },
                    { title: "Results" },
                  ]}
                />
              </div>
              <div className="md:hidden">
                <Steps
                  current={workflowCurrentStep}
                  direction="vertical"
                  size="small"
                  items={[
                    { title: "Upload" },
                    { title: "Options" },
                    { title: "Review" },
                    { title: "Processing" },
                    { title: "Results" },
                  ]}
                />
              </div>
            </div>
          )}

          {/* Past Jobs Toggle */}
          <div className="mb-6">
            <button
              onClick={() => setShowPastJobs(!showPastJobs)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                showPastJobs
                  ? "bg-brand-600 !text-white shadow-md [&>*]:!text-white"
                  : "bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-sm"
              }`}
            >
              <HistoryOutlined />
              {showPastJobs ? "Hide History" : "View History"}
            </button>
          </div>

          {/* Past Jobs Section */}
          <AnimatePresence>
            {showPastJobs && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mb-6 overflow-hidden"
              >
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <ViewPastProcessingJobs />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Grid - Document Upload & Sidebar */}
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1, delayChildren: 0.05 },
              },
            }}
          >
            {/* Main Content - Document Upload */}
            <motion.div
              className="lg:col-span-2"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { type: "spring", stiffness: 300, damping: 25 },
                },
              }}
            >
              <div
                className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden
                hover:shadow-lg hover:border-gray-300/80 transition-all duration-300"
              >
                <AddNewDocument />
              </div>
            </motion.div>

            {/* Sidebar - Credits & Purchase */}
            <motion.div
              className="space-y-5"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.08 },
                },
              }}
            >
              {/* Credit Balance Card */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: { type: "spring", stiffness: 300, damping: 25 },
                  },
                }}
              >
                {userInfo ? (
                  <CreditInformation userInfo={userInfo} isLoading={isLoadingUserInfo} />
                ) : (
                  <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <div className="flex justify-center items-center h-24">
                      <Spin size="large" />
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Promo Code Card */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: { type: "spring", stiffness: 300, damping: 25 },
                  },
                }}
              >
                <PromoCodes isLoading={isApplyingPromoCode} />
              </motion.div>

              {/* Purchase Credits */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden
                  hover:shadow-lg hover:border-gray-300/80 transition-all duration-300"
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: { type: "spring", stiffness: 300, damping: 25 },
                  },
                }}
              >
                {MemoizedCreditPurchase}
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Progress & Results Sections */}
          <AnimatePresence>
            <div ref={progressSectionRef}>
              {(isProcessingDocument || jobCompleted) && (
                <motion.div
                  key="progress"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="mt-6"
                >
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                    <ProgressTracker
                      onJobComplete={handleJobComplete}
                      generate_multiple_choice_quiz={generateMultipleChoiceQuiz}
                      generate_short_answer_quiz={generateShortAnswerQuiz}
                      generate_anki_flashcards={generateAnkiFlashcards}
                      generate_presentation_slides={generatePresentationSlides}
                      generate_summary={generateSummary}
                      generate_mindmap={generateMindmap}
                      generate_readability_analysis={generateReadabilityAnalysis}
                      generate_lesson_plan={generateLessonPlan}
                      isAdditionalGeneration={false}
                      existingJobStatus={undefined}
                      originalJobData={undefined}
                    />

                    {jobCompleted && (
                      <div className="mt-8 text-center py-8 bg-green-50 rounded-xl">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircleOutlined className="text-3xl text-green-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          Processing Complete!
                        </h3>
                        <p className="text-gray-600 mt-1">Your files are ready for download</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            <div ref={resultsSectionRef}>
              {resultsReady && currentJobId && (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="mt-6"
                >
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <ProcessedDocumentResultsViewer
                      job_id={currentJobId}
                      onUpdateFiles={(updatedFiles) => setProcessedFiles(updatedFiles)}
                    />
                  </div>
                </motion.div>
              )}
            </div>
          </AnimatePresence>
        </main>

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmProcessing}
          creditCost={creditCost}
          userInfo={userInfo}
          onBuyCredits={() => setShowConfirmModal(false)}
          documentInfo={
            documentInfo
              ? {
                  originalFileName: documentInfo.fileName || "",
                  originalFileSize: documentInfo.fileSize || 0,
                  totalPages: documentInfo.totalPages || 0,
                }
              : null
          }
        />

        {/* Mobile Drawer */}
        <Drawer
          title={null}
          placement="right"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={280}
          className="lg:hidden"
          styles={{
            header: { display: "none" },
            body: { padding: 0 },
          }}
        >
          <div className="p-4">
            {/* User Info */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white text-lg font-semibold">
                {userInitial}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{userName}</p>
                <p className="text-sm text-gray-500">Signed in</p>
              </div>
            </div>

            {/* Menu Items */}
            <div className="space-y-2">
              <Link href="/" onClick={() => setDrawerVisible(false)}>
                <button className="w-full h-12 px-4 flex items-center gap-3 text-left text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
                  <HomeOutlined className="text-lg text-gray-400" />
                  <span className="font-medium">Home</span>
                </button>
              </Link>

              {isAdmin && (
                <Link href="/admin" onClick={() => setDrawerVisible(false)}>
                  <button className="w-full h-12 px-4 flex items-center gap-3 text-left text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
                    <SettingOutlined className="text-lg text-gray-400" />
                    <span className="font-medium">Admin Portal</span>
                  </button>
                </Link>
              )}

              <hr className="my-4 border-gray-200" />

              <button
                onClick={() => signOut()}
                className="w-full h-12 px-4 flex items-center gap-3 text-left text-red-600 hover:bg-red-50 rounded-xl transition-colors"
              >
                <LogoutOutlined className="text-lg" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        </Drawer>
      </div>
    </ErrorBoundary>
  );
}
